import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { logActivity } from '@/lib/activityLog';
import type { Branch } from '@/lib/types';

interface BranchContextValue {
  branches: Branch[];
  loading: boolean;
  activeBranchId: string | null;
  activeBranch: Branch | null;
  setActiveBranchId: (id: string | null) => void;
  reload: () => void;
}

const BranchContext = createContext<BranchContextValue | undefined>(undefined);

export function BranchProvider({ children }: { children: ReactNode }) {
  const { profile, isAdmin, user } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeBranchId, setActiveBranchIdState] = useState<string | null>(null);

  const storageKey = user?.id ? `branch:active:${user.id}` : 'branch:active';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('branches').select('*').order('name');
      if (error) throw error;
      setBranches((data as Branch[]) ?? []);
    } catch {
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Cajero: sucursal bloqueada a la asignada en su perfil.
  // Además, si esa sucursal no está en la lista cargada (fallo de red, carga lenta),
  // la buscamos directamente para que el cajero NUNCA quede "Sin sucursal".
  useEffect(() => {
    if (isAdmin || !profile?.branch_id) return;
    setActiveBranchIdState(profile.branch_id);
    if (!branches.some((b) => b.id === profile.branch_id)) {
      supabase
        .from('branches')
        .select('*')
        .eq('id', profile.branch_id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setBranches((prev) =>
              prev.some((b) => b.id === (data as Branch).id) ? prev : [...prev, data as Branch]
            );
          }
        });
    }
  }, [isAdmin, profile?.branch_id, branches]);

  // Administrador: restaurar la sucursal guardada.
  useEffect(() => {
    if (!isAdmin) return;
    const stored = localStorage.getItem(storageKey);
    if (stored) setActiveBranchIdState(stored);
  }, [isAdmin, storageKey]);

  const setActiveBranchId = useCallback(
    (id: string | null) => {
      setActiveBranchIdState(id);
      if (id) localStorage.setItem(storageKey, id);
      else localStorage.removeItem(storageKey);
      const name = id ? branches.find((b) => b.id === id)?.name ?? id : 'Todas las sucursales';
      void logActivity({
        module: 'branches',
        action: 'branch_switch',
        description: `Sucursal activa: ${name}`,
        targetType: 'branch',
        targetId: id ?? undefined,
        branchId: id,
      });
    },
    [storageKey, branches]
  );

  const activeBranch = useMemo(
    () => branches.find((b) => b.id === activeBranchId) ?? null,
    [branches, activeBranchId]
  );

  const value = useMemo(
    () => ({ branches, loading, activeBranchId, activeBranch, setActiveBranchId, reload: load }),
    [branches, loading, activeBranchId, activeBranch, setActiveBranchId, load]
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error('useBranch debe usarse dentro de BranchProvider');
  return ctx;
}